import React, { useCallback, useEffect, useState } from 'react';
import { Table } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Dialog, DialogTrigger } from 'components/ui/dialog';
import DataTable from 'components/blocks/data-table/data-table';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { createOrganizationTableColumns } from 'features/organization/components/organization-table/organization-table-columns';
import { OrganizationTableToolbar } from 'features/organization/components/organization-table/organization-table-toolbar';
import { OrganizationData } from 'features/organization/services/organization-service';
import { useOrganizations, useUpdateOrganizationStatus } from 'features/organization/hooks/use-organization';
import { OrganizationDetails } from 'features/organization/components/organization-details/organization-details';
import { AddOrganization } from 'features/organization/components/add-organization/add-organization';
import { useIsMobile } from 'hooks/use-mobile';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { Navigate } from 'react-router-dom';
import { ExpandedOrganizationDetails } from 'features/organization/components/organization-details-mobile-view/expanded-organization-details';
import { ORGANIZATION_CONSTANTS } from 'constant/organization';
import { EditOrganizationDetails } from 'features/organization/components/edit-organization-details/edit-organization-details';


interface PaginationState {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
}

/**
 * Table toolbar component for handling filters and search.
 */
const TableToolbar = ({
    table,
    onSearch,
    columns,
}: {
    table: Table<OrganizationData>;
    onSearch: (filters: { name: string }) => void;
    columns: any[];
}) => {
    return <OrganizationTableToolbar table={table} onSearch={onSearch} columns={columns} />;
};

/**
 * Main Organization Management Table Page.
 * Renders organization data in a table with search, pagination, row expansion, and modals.
 */
export function OrganizationTablePage() {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const [openSheet, setOpenSheet] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState<OrganizationData | null>(null);
    const [isAddOrganizationModalOpen, setIsAddOrganizationModalOpen] = useState(false);
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
    const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
    const { t } = useTranslation();
    const isAdmin = useCommonStore((state) => state.isAdmin);
    const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);

    const [filters, setFilters] = useState({
        name: '',
    });

    const [paginationState, setPaginationState] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
        totalCount: 0,
    });

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const queryParams = {
        page: paginationState.pageIndex,
        pageSize: paginationState.pageSize,
        name: filters.name,
        enabled: isAdmin || isOrgAdmin,
    };

    // Fetch organizations based on query parameters
    const { data, isLoading, error } = useOrganizations(queryParams);

    // Status update mutation
    const updateStatusMutation = useUpdateOrganizationStatus({
        onSuccess: () => {
            setIsDeactivateModalOpen(false);
            setIsActivateModalOpen(false);
            setSelectedOrganization(null);
        }
    });

    // Filter out the default organization
    const filteredData = React.useMemo(() => {
        if (!data?.data) return [];
        return data.data.filter(org => org.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME);
    }, [data?.data]);

    /**
     * Handles pagination changes.
     */
    const handlePaginationChange = useCallback(
        (newPagination: { pageIndex: number; pageSize: number }) => {
            setPaginationState((prev) => ({
                ...prev,
                pageIndex: newPagination.pageIndex,
                pageSize: newPagination.pageSize,
            }));
        },
        []
    );

    /**
     * Updates total count when new data is available.
     */
    useEffect(() => {
        if (data?.totalCount !== undefined) {
            setPaginationState((prev) => ({
                ...prev,
                totalCount: data.totalCount,
            }));
        }
    }, [data?.totalCount]);

    /**
     * Handles search input changes and resets pagination.
     */
    const handleSearch = useCallback((newFilters: { name: string }) => {
        setFilters(newFilters);
        setPaginationState((prev) => ({
            ...prev,
            pageIndex: 0,
        }));
    }, []);

    /**
     * Disables scrolling when details modal is open.
     */
    useEffect(() => {
        if (openSheet) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [openSheet]);

    if (!isAdmin && !isOrgAdmin) {
        return <Navigate to="/home" />;
    }

    /**
     * Opens the organization details modal.
     */
    const handleViewDetails = (organization: OrganizationData) => {
        setSelectedOrganization(organization);
        setOpenSheet(true);
    };

    /**
     * Handles editing an organization.
     */
    const handleEdit = (organization: OrganizationData) => {
        navigate(`/organization-management/${organization.itemId}/edit`);
    };


    /**
     * Opens organization edit modal.
     */
    const openOrganizationModal = (organization: OrganizationData) => {
        setSelectedOrganization(organization);
        setIsEditModalOpen(true);
    };

    /**
     * Opens deactivate confirmation modal.
     */
    const handleDeactivate = (organization: OrganizationData) => {
        setSelectedOrganization(organization);
        setIsDeactivateModalOpen(true);
    };

    /**
     * Opens activate confirmation modal.
     */
    const handleActivate = (organization: OrganizationData) => {
        setSelectedOrganization(organization);
        setIsActivateModalOpen(true);
    };

    /**
     * Confirms organization deactivation.
     */
    const handleConfirmDeactivation = async () => {
        if (!selectedOrganization) return;

        updateStatusMutation.mutate({
            itemId: selectedOrganization.itemId,
            isActive: false
        });
    };

    /**
     * Confirms organization activation.
     */
    const handleConfirmActivation = async () => {
        if (!selectedOrganization) return;

        updateStatusMutation.mutate({
            itemId: selectedOrganization.itemId,
            isActive: true
        });
    };

    // Define table columns with actions
    const columns = createOrganizationTableColumns({
        onViewDetails: handleViewDetails,
        onEdit: handleEdit,
        /* onDeactivate: handleDeactivate,
        onActivate: handleActivate, */
        t,
    });

    // Show error message if data fetching fails
    if (error) {
        return <div className="p-4 text-error">{t('ERROR_LOADING_ORGANIZATIONS')}: {error.error?.message}</div>;
    }

    /**
     * Renders expanded row content for mobile view.
     */
    const renderExpandedContent = (organization: OrganizationData) => (
        <ExpandedOrganizationDetails
            organization={organization}
            onEdit={openOrganizationModal}
            onDeactivate={handleDeactivate}
            onActivate={handleActivate}
        />
    );

    /**
     * Renders table filter toolbar.
     */
    const renderFilterToolbar = (table: Table<OrganizationData>) => (
        <TableToolbar table={table} onSearch={handleSearch} columns={columns} />
    );


    /**
     * Closes the organization edit modal .
     */
    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
    };

    return (
        <div className="flex flex-col h-full w-full">
            <div className="h-full flex-col flex w-full gap-6 md:gap-8">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold tracking-tight">{t('ORGANIZATIONS')}</h2>

                    {isAdmin && <Dialog open={isAddOrganizationModalOpen} onOpenChange={setIsAddOrganizationModalOpen}>

                        <DialogTrigger asChild>
                            <Button variant="default" className="flex items-center">
                                <Plus size={20} />
                                {isMobile ? t('ADD') : t('ADD_ORGANIZATION')}
                            </Button>
                        </DialogTrigger>

                        {isAddOrganizationModalOpen && (
                            <AddOrganization
                                onClose={() => setIsAddOrganizationModalOpen(false)}
                            />
                        )}
                    </Dialog>}
                </div>

                <DataTable
                    data={filteredData}
                    columns={columns}
                    onRowClick={handleViewDetails}
                    isLoading={isLoading}
                    error={error}
                    toolbar={(table) => renderFilterToolbar(table)}
                    pagination={{
                        pageIndex: paginationState.pageIndex,
                        pageSize: paginationState.pageSize,
                        totalCount: paginationState.totalCount,
                    }}
                    onPaginationChange={handlePaginationChange}
                    manualPagination={true}
                    expandedContent={renderExpandedContent}
                    mobileColumns={['name']}
                    expandable={true}
                />
            </div>

            {selectedOrganization && (
                <OrganizationDetails
                    open={openSheet}
                    onOpenChange={setOpenSheet}
                    organization={selectedOrganization}
                />
            )}

            <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                {isEditModalOpen && (
                    <EditOrganizationDetails
                        organization={selectedOrganization!}
                        onClose={() => handleEditModalClose()}
                    />
                )}
            </Dialog>

            <ConfirmationModal
                open={isDeactivateModalOpen}
                onOpenChange={setIsDeactivateModalOpen}
                title={t('DEACTIVATE_ORGANIZATION')}
                description={t('DEACTIVATE_ORGANIZATION_CONFIRMATION')}
                onConfirm={handleConfirmDeactivation}
                confirmText={t('CONFIRM')}
                cancelText={t('CANCEL')}
            />

            <ConfirmationModal
                open={isActivateModalOpen}
                onOpenChange={setIsActivateModalOpen}
                title={t('ACTIVATE_ORGANIZATION')}
                description={t('ACTIVATE_ORGANIZATION_CONFIRMATION')}
                onConfirm={handleConfirmActivation}
                confirmText={t('CONFIRM')}
                cancelText={t('CANCEL')}
            />
        </div>
    );
} 
